import {
  CircleEllipsis,
  Globe,
  Mail,
  Megaphone,
  MessageCircle,
  MessagesSquare,
  Phone,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { instanteDoDiaEmSaoPaulo } from '@/shared/formatters/date';

import type {
  CanalManifestacao,
  ManifestacaoListItem,
  ParteCompletaItem,
  ParteElegivelManifestacao,
  RemetenteTipoManifestacao,
} from '@/features/secretaria-arbitral/types';

import { parteEstaAtiva } from './partesAcesso';
import { MS_DIA } from './prazoAndamento';

export const MAX_ANEXOS_MANIFESTACAO = 5;

export const MOTIVO_DESCONSIDERAR_MIN_CARACTERES = 10;

export const CANAL_MANIFESTACAO_PADRAO: CanalManifestacao = 'PLATAFORMA_CHAT';

interface CanalMeta {
  label: string;
  Icone: LucideIcon;
}

const CANAL_META: Record<CanalManifestacao, CanalMeta> = {
  PLATAFORMA_CHAT: { label: 'Chat da plataforma', Icone: MessagesSquare },
  PORTAL_WEB: { label: 'Portal web', Icone: Globe },
  WHATSAPP: { label: 'WhatsApp', Icone: MessageCircle },
  EMAIL: { label: 'E-mail', Icone: Mail },
  RECLAME_AQUI: { label: 'Reclame Aqui', Icone: Megaphone },
  TELEFONE: { label: 'Telefone', Icone: Phone },
  PRESENCIAL: { label: 'Presencial', Icone: Users },
  OUTRO: { label: 'Outro canal', Icone: CircleEllipsis },
};

function ehCanalConhecido(canal: string): canal is CanalManifestacao {
  return Object.prototype.hasOwnProperty.call(CANAL_META, canal);
}

export const CANAIS_MANIFESTACAO: CanalManifestacao[] =
  Object.keys(CANAL_META).filter(ehCanalConhecido);

export function canalMeta(canal: string): CanalMeta {
  return ehCanalConhecido(canal) ? CANAL_META[canal] : CANAL_META[CANAL_MANIFESTACAO_PADRAO];
}

const REMETENTE_TIPO_LABEL: Record<RemetenteTipoManifestacao, string> = {
  REQUERENTE: 'Requerente',
  REQUERIDO: 'Requerido',
  ADVOGADO_REQUERENTE: 'Advogado do requerente',
  ADVOGADO_REQUERIDO: 'Advogado do requerido',
  SECRETARIA: 'Secretaria',
  ARBITRO: 'Árbitro',
  SISTEMA: 'Sistema',
};

const REMETENTE_TIPO_DESCONHECIDO = 'Remetente';

function ehRemetenteTipoConhecido(tipo: string): tipo is RemetenteTipoManifestacao {
  return Object.prototype.hasOwnProperty.call(REMETENTE_TIPO_LABEL, tipo);
}

function rotuloRemetenteTipo(tipo: string): string {
  return ehRemetenteTipoConhecido(tipo) ? REMETENTE_TIPO_LABEL[tipo] : REMETENTE_TIPO_DESCONHECIDO;
}

export function descreverRemetente(manifestacao: ManifestacaoListItem): string {
  const papel = rotuloRemetenteTipo(manifestacao.remetenteTipo);
  const nome = manifestacao.remetente.nome?.trim();
  return nome ? `${nome} (${papel})` : papel;
}

const PAPEL_ELEGIVEL_LABEL: Record<string, string> = {
  REQUERENTE: 'Requerente',
  REQUERIDO: 'Requerido',
  ADVOGADO: 'Advogado',
};

export function partesElegiveisManifestacao(
  partes: ParteCompletaItem[],
): ParteElegivelManifestacao[] {
  return partes.filter(parteEstaAtiva).flatMap((parte) => {
    const papel = parte.papeisVigentes.find((vigente) =>
      Object.prototype.hasOwnProperty.call(PAPEL_ELEGIVEL_LABEL, vigente),
    );
    if (!papel) return [];
    return [
      {
        parteArbitralId: parte.id,
        nome: parte.pessoa.nomeCompleto,
        papel: PAPEL_ELEGIVEL_LABEL[papel],
      },
    ];
  });
}

/**
 * `dataRecebimento` do POST — **ausente quando a peça chegou hoje, de propósito.**
 *
 * A coluna é `timestamptz`, não `date`: mandar o dia de hoje em 00:00 joga o registro para o
 * fundo do dia, atrás de tudo que chegou hoje por canal com hora real, e a lista da Secretaria
 * passa a mentir sobre a ordem dos fatos. Sem o campo, o core usa `now()` — a hora real do ato —
 * e o registro cai na posição cronológica certa. Não é retrocompatibilidade acidental: é o
 * caminho certo para "chegou hoje". Quando o dia é outro, o dia **é** a informação (a Secretaria
 * não sabe a hora de um e-mail), e aí vale o 00:00 de São Paulo.
 */
export function dataRecebimentoParaEnviar(diaRecebimento: string, hoje: string): string | null {
  if (diaRecebimento === hoje) return null;
  return instanteDoDiaEmSaoPaulo(diaRecebimento);
}

/**
 * Quantos dias atrás está o recebimento informado, comparado ao dia de hoje em São Paulo.
 * `0` quando a Secretaria mantém o default (peça chegou hoje). É a métrica do registro
 * retroativo — vai para o RUM sem carregar a data em si.
 */
export function diasDeRetroatividade(diaRecebimento: string, hoje: string): number {
  return Math.round(
    (Date.parse(`${hoje}T00:00:00.000Z`) - Date.parse(`${diaRecebimento}T00:00:00.000Z`)) / MS_DIA,
  );
}

export type FiltroManifestacoes = 'REGISTRADAS' | 'DESCONSIDERADAS' | 'TODAS';

export const FILTROS_MANIFESTACOES: { value: FiltroManifestacoes; label: string }[] = [
  { value: 'REGISTRADAS', label: 'Registradas' },
  { value: 'DESCONSIDERADAS', label: 'Desconsideradas' },
  { value: 'TODAS', label: 'Todas' },
];

export function manifestacoesDoFiltro(
  items: ManifestacaoListItem[],
  filtro: FiltroManifestacoes,
): ManifestacaoListItem[] {
  if (filtro === 'REGISTRADAS') return items.filter((item) => item.status === 'REGISTRADA');
  if (filtro === 'DESCONSIDERADAS') return items.filter((item) => item.status === 'DESCONSIDERADA');
  return items;
}

export function contagemPorFiltro(
  items: ManifestacaoListItem[],
): Record<FiltroManifestacoes, number> {
  return {
    REGISTRADAS: manifestacoesDoFiltro(items, 'REGISTRADAS').length,
    DESCONSIDERADAS: manifestacoesDoFiltro(items, 'DESCONSIDERADAS').length,
    TODAS: items.length,
  };
}
